import { useState, useEffect } from "react";
import DateTimePicker from "../../components/DateTimePicker";
import pdfIcon from "../../assets/svg/pdf-icon.svg";
import {
  getPowerOfAttorneyContext,
  updatePowerOfAttorneyEntry,
  generatePowerOfAttorneyPdf,
  type PowerOfAttorneyItem,
} from "../../api/discussion";
import { toast } from "sonner";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { HiOutlineX } from "react-icons/hi";
import { downloadFileFromUrlWithFallback } from "../../utils/downloadFileFromUrl";
import { toLocalYmd } from "../../utils/filingApiDates";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/** Block + wrap so long spec refs + labels never sit on one rigid line over inputs */
const LABEL_STYLE =
  "text-xs font-normal leading-snug text-[#222222] w-full flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;
const INPUT_BOX =
  "box-border w-full bg-white border border-[#EEEEEE] rounded-[6px] focus-within:ring-1 focus-within:ring-[#222]";
/** Single-line inputs only — fixed height must not be used on textarea wrappers */
const INPUT_ROW =
  `${INPUT_BOX} flex flex-row items-center gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px]`;
const PLACEHOLDER_CLASS = "text-[12px] md:text-xs font-normal leading-5 text-[#6D6D6D]";
const TEXT_CLASS = "text-[12px] md:text-xs font-normal leading-5 text-[#222222]";

function markKey(classNumber: number, typeOfMark: string): string {
  return `${classNumber}::${typeOfMark}`;
}

function formatDateAsOrdinal(d: Date): string {
  const day = d.getDate();
  const suffix =
    day === 1 || day === 21 || day === 31
      ? "st"
      : day === 2 || day === 22
        ? "nd"
        : day === 3 || day === 23
          ? "rd"
          : "th";
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return `${day}${suffix} day of ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function ReadOnlyRow({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
}) {
  const align = multiline ? "items-start" : "items-baseline";
  return (
    <>
      <div className={`flex ${align} font-medium text-[#222]`}>{label}</div>
      <div className={`flex ${align} text-[#222]`}>:</div>
      <div
        className={`text-[#222] break-words min-w-0 ${
          multiline ? "whitespace-pre-line" : ""
        }`}
      >
        {value || <span className="text-[#6D6D6D]">—</span>}
      </div>
    </>
  );
}

function TextInputPlain({
  label,
  value,
  onChange,
  placeholder = "Text Here",
  specRef,
  required = false,
  invalid = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  specRef?: string;
  required?: boolean;
  invalid?: boolean;
}) {
  return (
    <div
      className="flex flex-1 min-w-0 flex-col w-full gap-1.5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label className={LABEL_STYLE} style={LABEL_FEATURES}>
        {specRef ? (
          <>
            <span className="shrink-0 text-[#6d6d6d] font-normal">{specRef}</span>
            <span className="min-w-0 break-words">
              {label}
              {required && <span className="text-[#E7000B]"> *</span>}
            </span>
          </>
        ) : (
          <span className="min-w-0 break-words">
            {label}
            {required && <span className="text-[#E7000B]"> *</span>}
          </span>
        )}
      </label>
      <div
        className={`${INPUT_BOX} flex flex-row items-center gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] ${
          invalid ? "border-[#E7000B] focus-within:ring-[#E7000B]" : ""
        }`}
      >
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={`min-w-0 flex-1 bg-transparent focus:outline-none ${
            value ? TEXT_CLASS : PLACEHOLDER_CLASS
          }`}
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        />
      </div>
      {invalid && (
        <p className="text-[11px] text-[#E7000B]">{label} is required.</p>
      )}
    </div>
  );
}

type PreparationOfPowerOfAttorneyViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function PreparationOfPowerOfAttorneyView({
  leadId,
  onSuccess,
}: PreparationOfPowerOfAttorneyViewProps) {
  const [powerOfAttorneys, setPowerOfAttorneys] = useState<PowerOfAttorneyItem[]>([]);
  const [selectedMarkKey, setSelectedMarkKey] = useState("");
  const [applicant, setApplicant] = useState("");
  const [applicantAddress, setApplicantAddress] = useState("");
  const [applicantType, setApplicantType] = useState("");
  const [signatoryName, setSignatoryName] = useState("");
  const [signatoryDesignation, setSignatoryDesignation] = useState("");
  const [mark, setMark] = useState("");
  const [dateOfPowerOfAttorney, setDateOfPowerOfAttorney] = useState<Date | null>(null);
  const [descriptionOfApplicant, setDescriptionOfApplicant] = useState("");

  const [loading, setLoading] = useState(!!leadId);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const isDateMissing = !dateOfPowerOfAttorney;
  const isDescriptionMissing = !descriptionOfApplicant.trim();

  const validateRequiredFields = (): boolean => {
    if (isDateMissing || isDescriptionMissing) {
      setShowValidationErrors(true);
      const missing: string[] = [];
      if (isDateMissing) missing.push("Date of Power of Attorney");
      if (isDescriptionMissing) missing.push("Description of Applicant");
      toast.error(`${missing.join(" and ")} ${missing.length > 1 ? "are" : "is"} required.`);
      return false;
    }
    return true;
  };

  const selectedEntry = powerOfAttorneys.find(
    (p) => markKey(p.classNumber, p.typeOfMark) === selectedMarkKey
  );

  const selectedClassDisplay = powerOfAttorneys.length
    ? [...new Set(powerOfAttorneys.map((p) => p.classNumber))].sort((a, b) => a - b).join(", ")
    : "";

  const dateFormatted =
    dateOfPowerOfAttorney
      ? formatDateAsOrdinal(dateOfPowerOfAttorney)
      : (selectedEntry?.dateOfPowerOfAttorneyFormatted ?? "");

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getPowerOfAttorneyContext(leadId)
      .then((res) => {
        if (cancelled) return;
        const items = res.powerOfAttorneys ?? [];
        setPowerOfAttorneys(items);
        if (items.length > 0) {
          const first = items[0];
          const key = markKey(first.classNumber, first.typeOfMark);
          setSelectedMarkKey(key);
          setApplicant(first.applicant ?? "");
          setApplicantAddress(first.applicantAddress ?? "");
          setApplicantType(first.applicantType ?? "");
          setSignatoryName(first.signatoryName ?? "");
          setSignatoryDesignation(first.signatoryDesignation ?? "");
          setMark(first.mark ?? "");
          setDateOfPowerOfAttorney(
            first.dateOfPowerOfAttorney ? new Date(first.dateOfPowerOfAttorney) : null
          );
          setDescriptionOfApplicant(first.descriptionOfApplicant ?? "");
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load Power of Attorney data");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!selectedMarkKey || powerOfAttorneys.length === 0) return;
    const entry = powerOfAttorneys.find(
      (p) => markKey(p.classNumber, p.typeOfMark) === selectedMarkKey
    );
    if (!entry) return;
    setApplicant(entry.applicant ?? "");
    setApplicantAddress(entry.applicantAddress ?? "");
    setApplicantType(entry.applicantType ?? "");
    setSignatoryName(entry.signatoryName ?? "");
    setSignatoryDesignation(entry.signatoryDesignation ?? "");
    setMark(entry.mark ?? "");
    setDateOfPowerOfAttorney(
      entry.dateOfPowerOfAttorney ? new Date(entry.dateOfPowerOfAttorney) : null
    );
    setDescriptionOfApplicant(entry.descriptionOfApplicant ?? "");
  }, [selectedMarkKey, powerOfAttorneys]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || !selectedEntry || saving) return;
    if (!validateRequiredFields()) return;
    setSaving(true);
    try {
      const res = await updatePowerOfAttorneyEntry(leadId, {
        applicant: applicant || undefined,
        applicantAddress: applicantAddress || undefined,
        applicantType: applicantType || undefined,
        signatoryName: signatoryName || undefined,
        signatoryDesignation: signatoryDesignation || undefined,
        mark: mark.trim() ? mark : undefined,
        dateOfPowerOfAttorney: dateOfPowerOfAttorney
          ? toLocalYmd(dateOfPowerOfAttorney)
          : undefined,
        descriptionOfApplicant: descriptionOfApplicant || undefined,
      });
      setPowerOfAttorneys(res.powerOfAttorneys ?? []);
      const updated = res.powerOfAttorneys?.[0];
      if (updated) {
        setApplicant(updated.applicant ?? "");
        setApplicantAddress(updated.applicantAddress ?? "");
        setApplicantType(updated.applicantType ?? "");
        setSignatoryName(updated.signatoryName ?? "");
        setSignatoryDesignation(updated.signatoryDesignation ?? "");
        setMark(updated.mark ?? "");
        setDateOfPowerOfAttorney(
          updated.dateOfPowerOfAttorney ? new Date(updated.dateOfPowerOfAttorney) : null
        );
        setDescriptionOfApplicant(updated.descriptionOfApplicant ?? "");
      }
      toast.success("Power of Attorney saved successfully.");
      setShowValidationErrors(false);
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!leadId) return;
    if (!validateRequiredFields()) return;
    setGenerating(true);
    try {
      // Persist latest fields so the API uses the same data as the screen (template 03 / applicant type).
      await updatePowerOfAttorneyEntry(leadId, {
        applicant: applicant || undefined,
        applicantAddress: applicantAddress || undefined,
        applicantType: applicantType || undefined,
        signatoryName: signatoryName || undefined,
        signatoryDesignation: signatoryDesignation || undefined,
        mark: mark.trim() ? mark : undefined,
        dateOfPowerOfAttorney: toLocalYmd(dateOfPowerOfAttorney),
        descriptionOfApplicant: descriptionOfApplicant || undefined,
      });
      await generatePowerOfAttorneyPdf(leadId);
      const res = await getPowerOfAttorneyContext(leadId);
      setPowerOfAttorneys(res.powerOfAttorneys ?? []);
      toast.success("Power of Attorney PDF generated successfully.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate PDF");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (): Promise<void> => {
    const pdf = selectedEntry?.pdf;
    if (!pdf?.url) {
      toast.error("Generate PDF first before downloading.");
      return;
    }
    setDownloadingPdf(true);
    try {
      const result = await downloadFileFromUrlWithFallback(
        pdf.url,
        pdf.filename ?? "power-of-attorney.pdf"
      );
      if (result === "local") {
        toast.success("PDF saved to your device.");
      } else {
        toast.info(
          "Opened the PDF in a new tab. Use Save or Save as to store it locally."
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePreview = async () => {
    if (!leadId) return;
    if (!validateRequiredFields()) return;
    setPreviewPdfUrl(null);
    setNumPages(null);
    if (selectedEntry?.pdf?.url) {
      setPreviewPdfUrl(selectedEntry.pdf.url);
      return;
    }
    setPreviewing(true);
    try {
      // Persist latest Power of Attorney details before generating preview
      if (selectedEntry) {
        const res = await updatePowerOfAttorneyEntry(leadId, {
          applicant: applicant || undefined,
          applicantAddress: applicantAddress || undefined,
          applicantType: applicantType || undefined,
          signatoryName: signatoryName || undefined,
          signatoryDesignation: signatoryDesignation || undefined,
          mark: mark.trim() ? mark : undefined,
          dateOfPowerOfAttorney: dateOfPowerOfAttorney
            ? toLocalYmd(dateOfPowerOfAttorney)
            : undefined,
          descriptionOfApplicant: descriptionOfApplicant || undefined,
        });
        setPowerOfAttorneys(res.powerOfAttorneys ?? []);
      }

      await generatePowerOfAttorneyPdf(leadId);
      const res = await getPowerOfAttorneyContext(leadId);
      setPowerOfAttorneys(res.powerOfAttorneys ?? []);
      const entry = (res.powerOfAttorneys ?? []).find(
        (p) => markKey(p.classNumber, p.typeOfMark) === selectedMarkKey
      );
      if (entry?.pdf?.url) {
        setPreviewPdfUrl(entry.pdf.url);
      } else {
        toast.error("Failed to generate PDF for preview.");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate preview");
    } finally {
      setPreviewing(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading Power of Attorney data...</p>
      </div>
    );
  }

  if (powerOfAttorneys.length === 0) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          No marks found. Please complete the confirmation step first.
        </p>
      </div>
    );
  }

  return (
    <form
      id="preparation-of-power-of-attorney-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Row 1: Date of Power of Attorney | Description of Applicant */}
      <div className="flex flex-col md:flex-row md:items-start w-full gap-4 md:gap-5">
        <div className="flex flex-1 min-w-0 flex-col w-full gap-1.5">
          <label className={LABEL_STYLE} style={LABEL_FEATURES}>
            <span className="min-w-0 break-words">
              Date of Power of Attorney
              <span className="text-[#E7000B]"> *</span>
            </span>
          </label>
          <DateTimePicker
            value={dateOfPowerOfAttorney}
            onChange={setDateOfPowerOfAttorney}
            showTime={false}
            buttonClassName={`min-h-[44px] md:min-h-0 rounded-[6px] text-[12px] md:text-xs ${
              showValidationErrors && isDateMissing
                ? "border-[#E7000B] focus:ring-[#E7000B]"
                : ""
            }`}
          />
          {showValidationErrors && isDateMissing && (
            <p className="text-[11px] text-[#E7000B]">
              Date of Power of Attorney is required.
            </p>
          )}
        </div>
        <TextInputPlain
          label="Description of Applicant"
          value={descriptionOfApplicant}
          onChange={setDescriptionOfApplicant}
          placeholder="Text Here"
          required
          invalid={showValidationErrors && isDescriptionMissing}
        />
      </div>

      {/* Read-only display section (source spec p. 46 – summary before Generate PDF) */}
      <div className="grid grid-cols-[max-content_max-content_minmax(0,1fr)] gap-x-3 gap-y-1.5 w-full rounded-[6px] border border-[#EEEEEE] bg-[#FCFCFC] px-3 py-2.5 text-[12px] leading-5">
        <ReadOnlyRow label="Mark" value={mark} />
        <ReadOnlyRow label="Applicant" value={applicant} />
        <ReadOnlyRow label="Applicant Address" value={applicantAddress} multiline />
        <ReadOnlyRow label="Selected Class" value={selectedClassDisplay} />
        <ReadOnlyRow label="Applicant Type" value={applicantType} />
        <ReadOnlyRow label="Signatory's Name" value={signatoryName} />
        <ReadOnlyRow label="Signatory's Designation" value={signatoryDesignation} />
        <ReadOnlyRow label="Date" value={dateFormatted} />
      </div>

      {/* Generate PDF + Download + Preview PDF */}
      <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2.5">
        <button
          type="button"
          onClick={handleGeneratePdf}
          disabled={generating}
          className="flex min-h-[44px] sm:h-9 items-center justify-center gap-2 rounded-[6px] border border-[#EEEEEE] bg-white px-4 py-2.5 sm:py-2 text-[12px] md:text-xs font-medium leading-5 text-[#222222] hover:bg-[#F7F7F7] disabled:opacity-60 disabled:cursor-not-allowed touch-manipulation"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          <img src={pdfIcon} alt="" className="h-[22px] w-[22px] shrink-0" />
          {generating ? "Generating..." : "Generate PDF"}
        </button>
        <button
          type="button"
          onClick={() => {
            void handleDownload();
          }}
          disabled={downloadingPdf}
          className="min-h-[44px] sm:h-9 rounded-[6px] bg-[#222222] px-4 py-2.5 sm:py-2 text-center text-[12px] md:text-xs font-medium leading-5 text-white hover:bg-[#333333] touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {downloadingPdf ? "Downloading…" : "Download"}
        </button>
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewing}
          className="min-h-[44px] sm:h-9 rounded-[6px] border border-[#EEEEEE] bg-white px-4 py-2.5 sm:py-2 text-center text-[12px] md:text-xs font-medium leading-5 text-[#222222] hover:bg-[#F7F7F7] disabled:opacity-60 touch-manipulation"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {previewing ? "Generating..." : "Preview PDF"}
        </button>
      </div>

      {/* PDF Preview modal - mobile friendly */}
      {previewPdfUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4">
          <div className="relative flex h-[95vh] sm:h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl overflow-hidden">
            <div className="flex shrink-0 items-center justify-between border-b border-[#e3e3e3] px-3 sm:px-4 py-3">
              <span className="text-xs sm:text-sm font-medium text-[#222] truncate pr-2" style={{ fontFamily: "Inter, sans-serif" }}>
                Power of Attorney PDF Preview
              </span>
              <button
                type="button"
                onClick={() => setPreviewPdfUrl(null)}
                className="flex size-10 sm:size-8 items-center justify-center rounded text-[#6d6d6d] hover:bg-[#eee] hover:text-[#222] touch-manipulation shrink-0"
                aria-label="Close"
              >
                <HiOutlineX className="size-5" strokeWidth={2} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto p-2 sm:p-4">
              <Document
                file={previewPdfUrl}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                loading={<p className="text-sm text-[#6d6d6d]" style={{ fontFamily: "Inter, sans-serif" }}>Loading PDF…</p>}
                error={<p className="text-sm text-red-600" style={{ fontFamily: "Inter, sans-serif" }}>Failed to load PDF.</p>}
                className="flex flex-col items-center gap-4"
              >
                {numPages != null &&
                  Array.from({ length: numPages }, (_, i) => (
                    <Page
                      key={i + 1}
                      pageNumber={i + 1}
                      width={Math.min(800, typeof window !== "undefined" ? window.innerWidth - 24 : 800)}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  ))}
              </Document>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}
